import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { Button } from "@/ui/Button";
import { Field, fieldInputClass } from "@/ui/modals/Modal";
import { toast } from "@/stores/toast.store";
import { getErrorMessage } from "@/lib/api-errors";
import { useLogin } from "./auth.hooks";

/** Mirrors the API `loginSchema` (apps/api/src/modules/auth/auth.schema.ts). */
const schema = z.object({
  email: z.string().email("Email invalide"),
  password: z.string().min(1, "Mot de passe requis"),
});

export function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const login = useLogin();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState<{ email?: string; password?: string }>({});

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const parsed = schema.safeParse({ email, password });
    if (!parsed.success) {
      const fieldErrors: { email?: string; password?: string } = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0];
        if (key === "email" || key === "password") fieldErrors[key] = issue.message;
      }
      setErrors(fieldErrors);
      return;
    }
    setErrors({});
    login.mutate(parsed.data, {
      onSuccess: () => {
        toast.success("Connexion réussie");
        navigate("/dashboard");
      },
      onError: (err) => toast.error(getErrorMessage(err, "Connexion échouée")),
    });
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-bg px-4">
      <div className="w-full max-w-[420px]">
        <div className="text-center mb-6">
          <div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-lg bg-primary text-white font-display font-bold text-xl">
            F
          </div>
          <h1 className="font-display text-[25px] font-bold text-text">{t("auth.title")}</h1>
          <p className="text-xs text-text2 mt-1">{t("auth.subtitle")}</p>
        </div>

        <form onSubmit={submit} className="bg-card border border-border2 rounded-lg p-7">
          <Field label={t("auth.email")} required error={errors.email}>
            <input
              className={fieldInputClass}
              type="email"
              autoFocus
              autoComplete="username"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="owner@fadagency.dz"
            />
          </Field>
          <Field label={t("auth.password")} required error={errors.password}>
            <input
              className={fieldInputClass}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </Field>
          <Button type="submit" className="w-full" loading={login.isPending}>
            {login.isPending ? `${t("common.loading")}` : t("auth.submit")}
          </Button>
        </form>

        <button
          type="button"
          onClick={() => navigate("/dashboard")}
          className="mt-4 w-full text-center text-[11px] text-text3 hover:text-text2 transition-colors"
        >
          Continuer en mode démo (données locales)
        </button>
      </div>
    </div>
  );
}
